// ===========================================
// Settings -> Enquiries: the onboarding checklist (enquiries Phase 6, plan §11a S3, S9)
// ===========================================
// The items every new student's check-in is given. Unlike sources and closing reasons, ADDING is
// the owner's too: everyone with `student_onboarding.read` sees the list, only the owner tier adds,
// renames, hides, restores or reorders. A change reaches students who start after it; a check-in
// already open keeps the items it was given. Bound to `/admin/settling-in/items`, never the
// enquiry options route.

import { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Chip,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowDownward as DownIcon,
  ArrowUpward as UpIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Visibility as ShowIcon,
  VisibilityOff as HideIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import type { ChecklistItemOption } from '../../../api/settlingIn.api';
import {
  useAddChecklistItem,
  useChecklistItems,
  useReorderChecklistItems,
  useUpdateChecklistItem,
} from '../../../hooks/useSettlingIn';
import { useOwnerTitle } from '../../../hooks/useOwnerTitle';
import { usePermissions } from '../../../hooks/usePermissions';

export const CHECKLIST_LABEL_MAX = 80;

export default function OnboardingChecklistPanel() {
  const { can, isOwner } = usePermissions();
  const ownerTitle = useOwnerTitle();
  const { enqueueSnackbar } = useSnackbar();
  const { data, isLoading, isError } = useChecklistItems({ includeHidden: isOwner }, can('student_onboarding', 'read'));
  const addMutation = useAddChecklistItem();
  const updateMutation = useUpdateChecklistItem();
  const reorderMutation = useReorderChecklistItems();
  const [newLabel, setNewLabel] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState('');

  const items: ChecklistItemOption[] = Array.isArray(data) ? data : [];
  const busy = addMutation.isPending || updateMutation.isPending || reorderMutation.isPending;

  const handleAdd = async () => {
    const label = newLabel.trim();
    // A second Enter while the first add is in flight would post twice.
    if (!label || addMutation.isPending) return;
    try {
      const { existed } = await addMutation.mutateAsync(label);
      if (existed) enqueueSnackbar(`"${label}" is already on the checklist`, { variant: 'info' });
      setNewLabel('');
    } catch {
      // The hook shows the server's reason.
    }
  };

  const move = (index: number, delta: -1 | 1) => {
    const ids = items.map((o) => o.id);
    const target = index + delta;
    if (target < 0 || target >= ids.length) return;
    [ids[index], ids[target]] = [ids[target], ids[index]];
    // EVERY item, hidden ones included, exactly once.
    reorderMutation.mutate(ids);
  };

  const saveRename = async (item: ChecklistItemOption) => {
    const label = editLabel.trim();
    if (!label || label === item.label) {
      setEditingId(null);
      return;
    }
    try {
      await updateMutation.mutateAsync({ optionId: item.id, patch: { label } });
      setEditingId(null);
    } catch {
      // The hook shows the server's reason.
    }
  };

  return (
    <Card variant="outlined" data-testid="onboarding-checklist-panel">
      <CardHeader
        title="Onboarding checklist"
        subheader="What the office checks for every new student in their first weeks. Changes reach students who start after the change."
      />
      <CardContent>
        {isLoading ? (
          <Skeleton variant="rectangular" height={120} />
        ) : isError ? (
          <Alert severity="error">Could not load the checklist. Please try again.</Alert>
        ) : (
          <Stack spacing={1}>
            {items.length === 0 && (
              <Typography color="text.secondary">No items: a new student&apos;s check-in is complete straight away.</Typography>
            )}
            {items.map((o, index) => (
              <Stack key={o.id} direction="row" spacing={1} alignItems="center" sx={{ opacity: o.isActive ? 1 : 0.6, minWidth: 0 }}>
                {editingId === o.id ? (
                  <>
                    <TextField
                      size="small"
                      fullWidth
                      autoFocus
                      value={editLabel}
                      inputProps={{ maxLength: CHECKLIST_LABEL_MAX, 'aria-label': `New name for ${o.label}` }}
                      onChange={(e) => setEditLabel(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          if (editLabel.trim() && !updateMutation.isPending) void saveRename(o);
                        } else if (e.key === 'Escape') {
                          setEditingId(null);
                        }
                      }}
                    />
                    <Tooltip title="Save name">
                      <span>
                        <IconButton
                          aria-label={`Save name for ${o.label}`}
                          size="small"
                          disabled={!editLabel.trim() || updateMutation.isPending}
                          onClick={() => void saveRename(o)}
                        >
                          <CheckIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Cancel">
                      <IconButton aria-label="Cancel rename" size="small" onClick={() => setEditingId(null)}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                ) : (
                  <>
                    <Typography sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{o.label}</Typography>
                    {!o.isActive && <Chip size="small" variant="outlined" label="Hidden" />}
                    {isOwner && (
                      <>
                        <Tooltip title="Move up">
                          <span>
                            <IconButton aria-label={`Move ${o.label} up`} size="small" disabled={index === 0 || busy} onClick={() => move(index, -1)}>
                              <UpIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Move down">
                          <span>
                            <IconButton
                              aria-label={`Move ${o.label} down`}
                              size="small"
                              disabled={index === items.length - 1 || busy}
                              onClick={() => move(index, 1)}
                            >
                              <DownIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Rename">
                          <span>
                            <IconButton
                              aria-label={`Rename ${o.label}`}
                              size="small"
                              disabled={busy}
                              onClick={() => {
                                setEditingId(o.id);
                                setEditLabel(o.label);
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={o.isActive ? 'Hide' : 'Restore'}>
                          <span>
                            <IconButton
                              aria-label={`${o.isActive ? 'Hide' : 'Restore'} ${o.label}`}
                              size="small"
                              disabled={busy}
                              onClick={() => updateMutation.mutate({ optionId: o.id, patch: { isActive: !o.isActive } })}
                            >
                              {o.isActive ? <HideIcon fontSize="small" /> : <ShowIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </>
                    )}
                  </>
                )}
              </Stack>
            ))}
            {isOwner ? (
              <Stack direction="row" spacing={1} sx={{ pt: 1 }}>
                <TextField
                  size="small"
                  fullWidth
                  label="Add to the checklist"
                  value={newLabel}
                  inputProps={{ maxLength: CHECKLIST_LABEL_MAX }}
                  onChange={(e) => setNewLabel(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void handleAdd();
                    }
                  }}
                />
                <Button variant="outlined" onClick={() => void handleAdd()} disabled={!newLabel.trim() || addMutation.isPending}>
                  Add
                </Button>
              </Stack>
            ) : (
              <Typography variant="caption" color="text.secondary">
                Only the {ownerTitle} can change the checklist.
              </Typography>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
